import { useEffect, useMemo, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import analyticsModule from '@/lib/analytics';
import type { EventName, EventPayloadMap, EmptyEventProps } from '@repo/analytics';

/**
 * Hook to automatically track page views
 */
export function usePageTracking() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastTrackedUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname) return;
    const search = searchParams?.toString();
    const url = search && search.length > 0 ? `${pathname}?${search}` : pathname;
    if (typeof window === 'undefined') return;

    // React StrictMode in dev intentionally runs effects twice; also protect against
    // rerenders that don't actually change the URL.
    if (lastTrackedUrlRef.current === url) return;
    lastTrackedUrlRef.current = url;

    analyticsModule.withAnalytics(a =>
      a.page(url, {
        path: url,
        referrer: document.referrer,
        title: document.title,
      }),
    );
  }, [pathname, searchParams]);
}

/**
 * Hook to get analytics instance
 */
export function useAnalytics() {
  // Return a thin wrapper that defers to the loaded analytics instance
  return useMemo(
    () => ({
      page: (url?: string, properties?: Record<string, any>) =>
        analyticsModule.withAnalytics(a => a.page(url, properties)),
      setUser: (userId: string, email?: string, name?: string, anonymousId?: string) =>
        analyticsModule.withAnalytics(a => {
          // Prefer identify when we have richer traits.
          if (name || anonymousId) {
            return a.identify(userId, {
              ...(email ? { email } : {}),
              ...(name ? { name } : {}),
              ...(anonymousId ? { anon_id: anonymousId } : {}),
            });
          }
          return a.setUser(userId, email);
        }),
      annotate: (annotation: Record<string, any>) =>
        analyticsModule.withAnalytics(a => {
          return a.annotate(annotation);
        }),
      track: <E extends EventName>(
        eventName: E,
        ...args: EventPayloadMap[E] extends EmptyEventProps
          ? [properties?: EventPayloadMap[E]]
          : [properties: EventPayloadMap[E]]
      ) =>
        analyticsModule.withAnalytics(a => {
          return a.track(eventName, ...(args as [any]));
        }),
      reset: () => analyticsModule.withAnalytics(a => a.reset()),
    }),
    [],
  );
}

/**
 * Hook to identify user when they log in
 */
export function useUserIdentification(user?: { id: string; email?: string; name?: string }) {
  const analytics = useAnalytics();
  useEffect(() => {
    if (user) {
      analytics.setUser(user.id, user.email, user.name);
    } else {
      // User logged out
      analytics.reset();
    }
  }, [analytics, user]);
}
